import React, {useState, useEffect} from 'react';
import {
  Wallet, TrendingUp, ArrowDownCircle, Percent, Info,
  Download, Plus, Landmark, FileText, HandCoins, ArrowRight,
  Calendar, Briefcase, Coins, Wallet2
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell
} from 'recharts';
import { Placeholder } from 'react-bootstrap';

import layoutStyles from '../Dashboard2/Dashboard2.module.css';
import savingsStyles from './LoanOverview.module.css';

import Sidebar2 from '../../Components/SideBar2/SideBar2';
import Header2 from '../../Components/Header2/Header2';
import axios from 'axios';
import { BASE_URL } from '../../Components/Api/Api';
import { useNavigate } from "react-router-dom";

const completeGrowthData = (apiData = []) => {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  return months.map((month) => {
    const item = apiData.find(
      (g) => g.month?.toLowerCase() === month.toLowerCase()
    );

    return {
      month,
      amount: Number(item?.amount) || 0,
    };
  });
};

// Palette used to color the loan breakdown pie slices when the API
// doesn't provide a color for each entry.
const breakdownPalette = ['#15803d', '#2563eb', '#ea580c', '#7c3aed', '#dc2626', '#0891b2'];

// Builds the 5 stat cards straight from the `summary` object returned by
// /member_loan_overview: { total_loans, month_loans, total_repayments, outstanding_balance, available_balance }


const formatMetricValue = (value, type = "number") => {
  const numericValue = Number(value);

  // Handle invalid values
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(numericValue)
  ) {
    return type === "currency" ? "₦0.00" : "0";
  }

  // Percent
  if (type === "percent") {
    return `${numericValue.toFixed(0)}%`;
  }

  // Currency
  if (type === "currency") {
    const absValue = Math.abs(numericValue);
    const sign = numericValue < 0 ? "-" : "";

    let formatted;

    if (absValue >= 1_000_000_000_000) {
      formatted = `${(absValue / 1_000_000_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}T`;
    } else if (absValue >= 1_000_000_000) {
      formatted = `${(absValue / 1_000_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}B`;
    } else if (absValue >= 1_000_000) {
      formatted = `${(absValue / 1_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}M`;
    } else if (absValue >= 1_000) {
      formatted = `${(absValue / 1_000)
        .toFixed(1)
        .replace(/\.0$/, "")}K`;
    } else {
      formatted = absValue.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return `${sign}₦${formatted}`;
  }

  // Normal numbers
  if (Number.isInteger(numericValue)) {
    return numericValue.toLocaleString("en-NG");
  }

  return numericValue.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const buildStatData = (summary = {}) => ([
  {
    id: 1,
    label: 'Total Loans',
    value: formatMetricValue(summary.total_loans, "currency"),
    sub: 'All time loans',
    icon: Wallet,
    iconStyle: savingsStyles.iconGreen,
  },
  {
    id: 2,
    label: 'This Month Loans',
    value: formatMetricValue(summary.month_loans,  "currency"),
    sub: 'Loans taken this month',
    icon: Calendar,
    iconStyle: savingsStyles.iconGreen,
  },
  {
    id: 3,
    label: 'Total Repayments',
    value: formatMetricValue(summary.total_repayments, "currency"),
    sub: 'Repaid to date',
    icon: HandCoins,
    iconStyle: savingsStyles.iconBlue,
  },
  {
    id: 4,
    label: 'Outstanding Balance',
    value: formatMetricValue(summary.outstanding_balance, "currency"),
    sub: 'Amount remaining',
    icon: ArrowDownCircle,
    iconStyle: savingsStyles.iconOrange,
  },
  // {
  //   id: 5,
  //   label: 'Available Balance',
  //   value: formatMetricValue(summary.available_balance, "currency"),
  //   sub: 'Ready to use',
  //   icon: Briefcase,
  //   iconStyle: savingsStyles.iconPurple,
  // },
]);

// Normalizes whatever shape `loan_breakdown` comes back in into
// { name, value, amount, color } so the pie chart + legend always work,
// even while the API returns an empty array.
const buildBreakdownData = (breakDown = []) =>
  breakDown.map((item, idx) => ({
    name: item.label || item.name || item.type || 'Other',
    value: Number(item.percentage ?? item.value ?? item.percent ?? 0),
    amount: Number(item.amount ?? item.total ?? 0),
    color: item.color || breakdownPalette[idx % breakdownPalette.length],
  }));

  

export default function LoanOverview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [summary, setSummary] = useState({});
  const [tableData, setTableData] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [breakDown, setBreakDown] = useState([]);

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(0)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value;
  };

const formatPointLabel = (value) => {
  const amount = Number(value) || 0;

  if (amount === 0) return "0.00";

  if (amount >= 1000000) {
    return `₦${(amount / 1000000).toFixed(1)}M`;
  }

  if (amount >= 1000) {
    return `₦${(amount / 1000).toFixed(0)}K`;
  }

  return `₦${amount}`;
};

  const formatCurrency = (value) =>
    Number(value ?? 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const fetchMetric = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const companyid = localStorage.getItem('companyId');
      const memberid = localStorage.getItem("memberId");

      const res = await axios.get(
        `${BASE_URL}/member_loan_overview?company_id=${companyid}&member_id=${memberid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        }
      );

      const metric = res.data?.data || {};

      setStats(metric.summary || {});
      setSummary(metric.summary || {});
      setTableData(metric.recent_transactions || []);
      setGrowth(completeGrowthData(metric.growth || []));
      setBreakDown(metric.loan_breakdown || []);
    } catch (error) {
      console.error(error);
      setStats({});
      setSummary({});
      setTableData([]);
      setGrowth([]);
      setBreakDown([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLedger = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const companyid = localStorage.getItem("companyId");
      const memberid = localStorage.getItem("memberId");

      let url = `${BASE_URL}/my_loan_ledger?company_id=${companyid}&member_id=${memberid}`;

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = res.data?.data || {};
      setLedgers(data.ledger || []);
    } catch (error) {
      console.error(error);
      setLedgers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetric();
    fetchLedger();
  }, []);

    // ---- derived flags ----
  const tableHasData = Array.isArray(ledgers) && ledgers.length > 0;
  // Show only the latest 5 ledger records
const recentLedgers = [...ledgers]
  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  .slice(0, 5);

/*
const getFirstFiveLedgers = () => {
  return [...ledgers]
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .slice(0, 5);
};
*/

  const statData = buildStatData(stats, formatCurrency);
  const pieData = buildBreakdownData(breakDown);
  const pieTotal = pieData.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div className={layoutStyles.container}>
      <Sidebar2 />
      <main className={layoutStyles.mainContent}>
        <Header2 />

        <div className={layoutStyles.workspace}>
          <div className={savingsStyles.savingsPage}>

            {/* 1. STAT CARDS */}
            <div className={savingsStyles.statGrid}>
              {loading
                ? Array.from({ length: 4 }).map((_, idx) => (
                    <div key={idx} className={savingsStyles.statCard}>
                      {/* <div className={savingsStyles.statLabelRow}>
                        <Placeholder as="p" animation="glow" style={{ width: '60%', margin: 0 }}>
                          <Placeholder xs={8} />
                        </Placeholder>
                      </div> */}
                      <div  className={savingsStyles.statValueRow}>
                        <Placeholder animation="glow">
                          <Placeholder
                            xs={4}
                            bg="secondary"
                            style={{ height: '40px', width: '40px', borderRadius: '50%' }}
                          />
                        </Placeholder>
                        <Placeholder as="h4" animation="glow" style={{ flex: 1, margin: "10px 0 0 0" }}>
                          <Placeholder xs={7} size="lg" />
                        </Placeholder>
                      </div>
                      <Placeholder as="p" animation="glow">
                        <Placeholder xs={6} />
                      </Placeholder>
                      <div className={savingsStyles.statLabelRow}>
                        <Placeholder as="p" animation="glow" style={{ width: '60%', margin: 0 }}>
                          <Placeholder xs={8} />
                        </Placeholder>
                      </div>
                    </div>
                  ))
                : statData.map((item) => (
                    <div key={item.id} className={savingsStyles.statCard}>
                      {/* ROW 1: Label & Info Icon */}
                      <div className={savingsStyles.statLabelRow}>
                          <div className={`${savingsStyles.statIconWrapper} ${item.iconStyle}`}>
                          <item.icon size={24} />
                        </div>
                        {/* <Info size={16} className={savingsStyles.infoIcon} /> */}
                      </div>

                      {/* ROW 2: Metric Icon & Figure */}
                      <div className={savingsStyles.statValueRow}>
                      <p className={savingsStyles.statLabel}>{item.label}</p>
                        <h4 className={savingsStyles.statValue}>{item.value}</h4>
                      </div>

                      {/* ROW 3: Subtext / Trend */}
                      {item.sub && <p className={savingsStyles.statSub}>{item.sub}</p>}
                      {item.trend && (
                        <p className={`${savingsStyles.statSub} ${savingsStyles.trendUp}`}>
                          <TrendingUp size={14} /> {item.trend}
                        </p>
                      )}
                    </div>
                  ))}
            </div>

            {/* 2. CHARTS GRID */}
            <div className={savingsStyles.chartGrid}>
              {/* Loans Growth */}
              <div className={savingsStyles.card}>
                <div className={savingsStyles.cardHeader}>
                  <h3 className={savingsStyles.cardTitle}>Loans Growth (This Year)</h3>
                  {/* <select className={savingsStyles.selectDropdown}>
                    <option>This Year</option>
                  </select> */}
                </div>
                <div className={savingsStyles.chartWrapper}>
                  {loading ? (
                    <Placeholder as="div" animation="wave">
                      <Placeholder
                        xs={12}
                        bg="secondary"
                        style={{ height: '150px', width: '100%', borderRadius: '8px' }}
                      />
                    </Placeholder>
                  ) : (
                                         <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={growth}
                        margin={{ top: 25, right: 25, left: 15, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorGrowthScoping" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#15803d" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#15803d" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                    
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                          stroke="#f1f5f9"
                        />
                    
                        <XAxis
                          dataKey="month"
                          tick={{ fontSize: 10, fill: "#94a3b8" }}
                          axisLine={false}
                          tickLine={false}
                        />
                    
                        <YAxis hide />
                    
                        <Tooltip
                          formatter={(value) => `₦${Number(value).toLocaleString()}`}
                        />
                    
                        <Area
                          type="monotone"
                          dataKey="amount"
                          stroke="#15803d"
                          strokeWidth={2}
                          fillOpacity={1}
                          fill="url(#colorGrowthScoping)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
                {/* <a href="#" className={savingsStyles.viewLink}>
                  <span>View Full Report</span> <ArrowRight size={14} />
                </a> */}
              </div>

              {/* Loans Breakdown */}
              <div className={savingsStyles.card}>
                <div className={savingsStyles.cardHeader}>
                  <h3 className={savingsStyles.cardTitle}>Loans Breakdown</h3>
                </div>

                {loading ? (
                  <div className={savingsStyles.breakdownRow}>
                    <div className={savingsStyles.breakdownPieWrapper}>
                      <Placeholder as="div" animation="wave">
                        <Placeholder
                          bg="secondary"
                          style={{ height: '160px', width: '160px', borderRadius: '50%', margin: '0 auto', display: 'block' }}
                        />
                      </Placeholder>
                    </div>
                    <div className={savingsStyles.breakdownLegend}>
                      {Array.from({ length: 2 }).map((_, idx) => (
                        <div key={idx} className={savingsStyles.breakdownLegendItem}>
                          <Placeholder
                            animation="glow"
                            bg="secondary"
                            style={{ height: '10px', width: '10px', borderRadius: '50%' }}
                          />
                          <div className={savingsStyles.legendTextContent} style={{ flex: 1 }}>
                            <Placeholder as="span" animation="glow" style={{ display: 'block' }}>
                              <Placeholder xs={6} />
                            </Placeholder>
                            <Placeholder as="span" animation="glow" style={{ display: 'block' }}>
                              <Placeholder xs={4} />
                            </Placeholder>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : pieData.length === 0 ? (
                  <div className={savingsStyles.breakdownRow}>
                    <p className={savingsStyles.statSub}>No loan breakdown data available yet.</p>
                  </div>
                ) : (
                  <div className={savingsStyles.breakdownRow}>
                    <div className={savingsStyles.breakdownPieWrapper}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={55}
                            outerRadius={75}
                            paddingAngle={3}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${value}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className={savingsStyles.pieCenterText}>
                        <h4>N{formatCurrency(pieTotal || stats.total_loans)}</h4>
                        <p>Total Loans</p>
                      </div>
                    </div>

                    <div className={savingsStyles.breakdownLegend}>
                      {pieData.map((item, idx) => (
                        <div key={idx} className={savingsStyles.breakdownLegendItem}>
                          <span
                            className={savingsStyles.legendDot}
                            style={{ backgroundColor: item.color }}
                          ></span>
                          <div className={savingsStyles.legendTextContent}>
                            <span className={savingsStyles.legendTitle}>{item.name}</span>
                            <span className={savingsStyles.legendSub}>
                              N{formatCurrency(item.amount)} <span className={savingsStyles.legendPercent}>({item.value}%)</span>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* <a href="#" className={savingsStyles.viewLink}>
                  <span>View Breakdown</span> <ArrowRight size={14} />
                </a> */}
              </div>
            </div>

            {/* 3. ACCOUNT SUMMARY & TRANSACTIONS */}
            <div className={savingsStyles.infoGrid}>
              <div className={savingsStyles.card}>
                <h3 className={savingsStyles.cardTitle} style={{ marginBottom: '1rem' }}>Account Summary</h3>
                <div className={savingsStyles.summaryTable}>
                  {loading
                    ? Array.from({ length: 5 }).map((_, idx) => (
                        <div key={idx} className={savingsStyles.summaryRow}>
                          <Placeholder as="span" animation="glow" style={{ width: '45%' }}>
                            <Placeholder xs={10} />
                          </Placeholder>
                          <Placeholder as="span" animation="glow" style={{ width: '30%' }}>
                            <Placeholder xs={10} />
                          </Placeholder>
                        </div>
                      ))
                    : [
                        { label: 'Total Loans', value: `₦${formatCurrency(summary.total_loans)}` },
                        { label: 'This Month Loans', value: `₦${formatCurrency(summary.month_loans)}` },
                        { label: 'Total Repayments', value: `₦${formatCurrency(summary.total_repayments)}` },
                        { label: 'Outstanding Balance', value: `₦${formatCurrency(summary.outstanding_balance)}` },
                        {/* { label: 'Available Balance', value: `₦${formatCurrency(summary.available_balance)}` }, */}
                      ].map((row, idx) => (
                        <div key={idx} className={savingsStyles.summaryRow}>
                          <span className={savingsStyles.summaryLabel}>{row.label}</span>
                          <span className={savingsStyles.summaryValue}>{row.value}</span>
                        </div>
                      ))}
                </div>
                {/* <button className={savingsStyles.downloadBtn}>
                  <span>Download Statement</span>
                  <Download size={16} />
                </button> */}
              </div>

              <div className={savingsStyles.card}>
                <div className={savingsStyles.cardHeader} style={{ marginBottom: '0.5rem' }}>
                  <h3 className={savingsStyles.cardTitle}>Recent Transactions</h3>
                  <p className={savingsStyles.viewLink} 
                  onClick={() => navigate("/loans/ledger")}
                  style={{ marginTop: 0 }}
                  >
                  View All</p>
                </div>
                <div className={savingsStyles.tableWrapper}>
                  <table className={savingsStyles.transactionTable}>
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Description</th>
                        <th style={{ textAlign: "right" }}>Debit</th>
                        <th style={{ textAlign: "right" }}>Credit</th>
                        <th style={{ textAlign: "right" }}>Balance</th>
                      </tr>
                    </thead>
                  <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx}>
                      <td>
                        <Placeholder as="span" animation="glow">
                          <Placeholder xs={6} />
                        </Placeholder>
                      </td>
                      <td>
                        <Placeholder as="span" animation="glow">
                          <Placeholder xs={9} />
                        </Placeholder>
                      </td>
                      <td>
                        <Placeholder as="span" animation="glow">
                          <Placeholder xs={7} />
                        </Placeholder>
                      </td>
                      <td>
                        <Placeholder as="span" animation="glow">
                          <Placeholder xs={7} />
                        </Placeholder>
                      </td>
                      <td>
                        <Placeholder as="span" animation="glow">
                          <Placeholder xs={7} />
                        </Placeholder>
                      </td>
                    </tr>
                  ))
                ) : !tableHasData ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No transactions found.
                    </td>
                  </tr>
                ) : (
                  recentLedgers.map((tx, idx) => (
                    <tr key={tx.id}>
                      <td>{idx + 1}</td>
                      <td>{tx.description}</td>
                      <td style={{ textAlign: "right" }}>
                        {formatCurrency(tx.debit)}
                      </td>
                      <td style={{ textAlign: "right" }} >
                        {formatCurrency(tx.credit)}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {formatCurrency(tx.balance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
                  </table>
                </div>
                {/* <a href="#" className={savingsStyles.viewAllLink}>
                  <span>View All Transactions</span> <ArrowRight size={14} />
                </a> */}
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
}
